import React, { useState, useRef } from 'react'

const GripSvg = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
    <circle cx="3" cy="3" r="1.2"/><circle cx="7" cy="3" r="1.2"/>
    <circle cx="3" cy="7" r="1.2"/><circle cx="7" cy="7" r="1.2"/>
    <circle cx="3" cy="11" r="1.2"/><circle cx="7" cy="11" r="1.2"/>
  </svg>
)

const ChevSvg = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

function TimeControl({ hrs, hoursPerDay, onChange }) {
  const d = Math.floor(hrs / hoursPerDay)
  const h = Math.round(hrs % hoursPerDay)
  const [editD, setEditD] = useState(false)
  const [editH, setEditH] = useState(false)
  const [draftD, setDraftD] = useState('')
  const [draftH, setDraftH] = useState('')

  return (
    <div className="bi-time" onClick={e => e.stopPropagation()}>
      <button className="bi-time-btn" tabIndex={-1} onClick={() => onChange(Math.max(0, hrs - 1))}>−</button>
      <div className="bi-time-vals">
        <input
          className="bi-time-inp"
          type="number"
          style={{ width: d >= 100 ? '4ch' : d >= 10 ? '3ch' : '2ch' }}
          value={editD ? draftD : d}
          onFocus={e => { setEditD(true); setDraftD(String(d)); requestAnimationFrame(() => e.target.select()) }}
          onChange={e => setDraftD(e.target.value)}
          onBlur={() => { setEditD(false); const v = parseInt(draftD, 10); if (!isNaN(v)) onChange(Math.max(0, v) * hoursPerDay + h) }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
        />
        <sup className="bi-time-sup">d</sup>
        <span className="bi-time-sep">·</span>
        <input
          className="bi-time-inp"
          type="number"
          style={{ width: '2ch' }}
          value={editH ? draftH : h}
          onFocus={e => { setEditH(true); setDraftH(String(h)); requestAnimationFrame(() => e.target.select()) }}
          onChange={e => setDraftH(e.target.value)}
          onBlur={() => { setEditH(false); const v = parseInt(draftH, 10); if (!isNaN(v)) onChange(d * hoursPerDay + Math.min(hoursPerDay - 1, Math.max(0, v))) }}
          onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
        />
        <sup className="bi-time-sup">h</sup>
      </div>
      <button className="bi-time-btn" tabIndex={-1} onClick={() => onChange(hrs + 1)}>+</button>
    </div>
  )
}

function fmtTime(hrs, hoursPerDay) {
  const d = Math.floor(hrs / hoursPerDay)
  const h = Math.round(hrs % hoursPerDay)
  if (d === 0 && h === 0) return '—'
  if (h === 0) return `${d}d`
  if (d === 0) return `${h}h`
  return `${d}d ${h}h`
}

/**
 * Shared B&I Calculator sheet (P7 overlay).
 *
 * context='project' — edits time_tasks for a project; shows day rate, £ total, My Items integration.
 * context='library' — edits task_catalogue; shows per-unit multiplier toggle, no £ total.
 *
 * tasks prop is a normalized array: { id, name, section, hrs, note?, isMultiplied? }
 * onReorderTasks receives the full reordered normalized array (section may be updated on cross-section drop).
 * onReorderSections receives the new string[] of section names.
 */
export default function BiCalculator({
  sections = [],
  tasks = [],
  context = 'project',
  hoursPerDay = 8,
  dayRate = 350,
  subtitle,
  onTaskName,
  onTaskHrs,
  onTaskNote,
  onTaskMultiplied,
  onTaskDelete,
  onTaskAdd,
  onReorderTasks,
  onSectionAdd,
  onSectionDelete,
  onSectionRename,
  onReorderSections,
  taskCatalogue,
  onAddFromCatalogue,
  onSaveToLibrary,
  onHoursPerDayChange,
  onDayRateChange,
  onClose,
}) {
  const [collapsed, setCollapsed] = useState(new Set())
  const [editingSection, setEditingSection] = useState(null)
  const [addingSection, setAddingSection] = useState(false)
  const [newSectionName, setNewSectionName] = useState('')
  const [myItemsSection, setMyItemsSection] = useState(null)

  // drag state
  const [dragTaskIdx, setDragTaskIdx] = useState(null)
  const [dragOverTaskIdx, setDragOverTaskIdx] = useState(null)
  const [dragSectionIdx, setDragSectionIdx] = useState(null)
  const [dragOverSectionIdx, setDragOverSectionIdx] = useState(null)
  const taskDragRef = useRef(null)
  const sectionDragRef = useRef(null)

  const isLibrary = context === 'library'
  const taskCols = isLibrary
    ? '22px minmax(0,1fr) 180px 110px 60px'
    : '22px minmax(0,1fr) 180px minmax(80px,1fr) 60px'

  const totalHrs = tasks.reduce((s, t) => s + (t.hrs || 0), 0)
  const totalDays = totalHrs / hoursPerDay
  const totalCost = totalDays * dayRate

  const toggleSection = name => setCollapsed(prev => {
    const s = new Set(prev)
    if (s.has(name)) s.delete(name); else s.add(name)
    return s
  })

  const isTaskSaved = task =>
    (taskCatalogue || []).some(c => (c.name || '').toLowerCase() === (task.name || '').toLowerCase())

  const myItemsFor = section => {
    if (!taskCatalogue) return []
    const existing = new Set(tasks.map(t => t.name.toLowerCase()))
    return taskCatalogue.filter(ci => {
      const ph = (ci.phase || '').toLowerCase()
      const sl = section.toLowerCase()
      return (ph === sl || sl.includes(ph) || ph.includes(sl.split(' ')[0])) &&
        !existing.has((ci.name || '').toLowerCase())
    })
  }

  // task drag
  const handleTaskDragStart = idx => { taskDragRef.current = idx; setDragTaskIdx(idx) }
  const handleTaskDrop = dropIdx => {
    const from = taskDragRef.current
    if (from === null || from === dropIdx) { setDragTaskIdx(null); setDragOverTaskIdx(null); return }
    const reordered = [...tasks]
    const [moved] = reordered.splice(from, 1)
    const dropTask = tasks[dropIdx]
    if (dropTask && moved.section !== dropTask.section) moved.section = dropTask.section
    reordered.splice(dropIdx, 0, moved)
    onReorderTasks && onReorderTasks(reordered)
    setDragTaskIdx(null); setDragOverTaskIdx(null); taskDragRef.current = null
  }

  // section drag
  const handleSectionDragStart = idx => { sectionDragRef.current = idx; setDragSectionIdx(idx) }
  const handleSectionDrop = dropIdx => {
    const from = sectionDragRef.current
    if (from === null || from === dropIdx) { setDragSectionIdx(null); setDragOverSectionIdx(null); return }
    const reordered = [...sections]
    const [moved] = reordered.splice(from, 1)
    reordered.splice(dropIdx, 0, moved)
    onReorderSections && onReorderSections(reordered)
    setDragSectionIdx(null); setDragOverSectionIdx(null); sectionDragRef.current = null
  }

  return (
    <>
      <div className="bi-scrim" onClick={onClose} />
      <div style={{ position: 'fixed', inset: 0, zIndex: 81, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px', pointerEvents: 'none' }}>
        <div className="bi-sheet-inner" style={{ pointerEvents: 'all' }} onClick={e => e.stopPropagation()}>

          {/* ── Header ── */}
          <div className="bi-head">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <div style={{ flexShrink: 0 }}>
              <div className="bi-head-title">Build &amp; Install Calculator</div>
              {subtitle && <div className="bi-head-sub">{subtitle}</div>}
            </div>
            <div className="bi-head-fields">
              {!isLibrary && (
                <>
                  <div className="bi-head-field">
                    <span className="bi-head-lbl">Day rate</span>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>£</span>
                    <input
                      className="ak-edit ak-edit--num"
                      style={{ width: 52 }}
                      type="number"
                      defaultValue={dayRate}
                      onBlur={e => onDayRateChange && onDayRateChange(parseFloat(e.target.value) || 350, true)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                    />
                  </div>
                  <div className="bi-head-field">
                    <span className="bi-head-lbl">Hrs / day</span>
                    <input
                      className="ak-edit ak-edit--num"
                      style={{ width: 36 }}
                      type="number"
                      defaultValue={hoursPerDay}
                      onBlur={e => onHoursPerDayChange && onHoursPerDayChange(parseFloat(e.target.value) || 8, true)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                    />
                  </div>
                </>
              )}
              <button className="ak-btn ak-btn--primary ak-btn--sm" onClick={onClose}>Done</button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="bi-body">
            {sections.map((section, sIdx) => {
              const sectionTasks = tasks.filter(t => t.section === section)
              const sectionHrs = sectionTasks.reduce((s, t) => s + (t.hrs || 0), 0)
              const isCollapsed = collapsed.has(section)
              const catItems = !isLibrary ? myItemsFor(section) : []

              return (
                <div
                  key={section}
                  style={{
                    opacity: dragSectionIdx === sIdx ? 0.4 : 1,
                    borderTop: dragOverSectionIdx === sIdx ? '2px solid var(--green)' : undefined,
                  }}
                  onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverSectionIdx(sIdx) }}
                  onDrop={e => { e.preventDefault(); e.stopPropagation(); handleSectionDrop(sIdx) }}
                  onDragEnd={() => { setDragSectionIdx(null); setDragOverSectionIdx(null) }}
                >
                  {/* Band */}
                  <div className="bi-band" onClick={() => toggleSection(section)}>
                    {!isLibrary && (
                      <div
                        className="bi-band-grip"
                        draggable
                        onDragStart={e => { e.stopPropagation(); handleSectionDragStart(sIdx) }}
                        onClick={e => e.stopPropagation()}
                      >
                        <GripSvg />
                      </div>
                    )}

                    {editingSection === section ? (
                      <input
                        className="ak-input"
                        style={{ flex: 1, fontSize: 12, padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '1px' }}
                        defaultValue={section}
                        autoFocus
                        onClick={e => e.stopPropagation()}
                        onBlur={e => { onSectionRename && onSectionRename(section, e.target.value); setEditingSection(null) }}
                        onKeyDown={e => {
                          e.stopPropagation()
                          if (e.key === 'Enter') { onSectionRename && onSectionRename(section, e.target.value); setEditingSection(null) }
                          if (e.key === 'Escape') setEditingSection(null)
                        }}
                      />
                    ) : (
                      <span
                        className="bi-band-name"
                        onDoubleClick={e => { e.stopPropagation(); if (!isLibrary) setEditingSection(section) }}
                        title={!isLibrary ? 'Double-click to rename' : undefined}
                      >{section}</span>
                    )}

                    <span style={{
                      background: 'var(--surface-3)', color: 'var(--ink-3)', fontFamily: 'var(--mono)',
                      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, border: '1px solid var(--line-2)',
                    }}>{sectionTasks.length}</span>

                    <span className="bi-band-total">{fmtTime(sectionHrs, hoursPerDay)}</span>

                    {!isLibrary && sIdx > 0 && (
                      <button
                        className="ak-iconbtn ak-iconbtn--danger"
                        style={{ width: 22, height: 22, flexShrink: 0 }}
                        onClick={e => { e.stopPropagation(); onSectionDelete && onSectionDelete(section) }}
                        title="Delete section"
                      >×</button>
                    )}

                    <span className={'bi-band-chev' + (isCollapsed ? ' collapsed' : '')}>
                      <ChevSvg />
                    </span>
                  </div>

                  {/* Tasks */}
                  {!isCollapsed && (
                    <>
                      <div className="bi-th" style={{ gridTemplateColumns: taskCols }}>
                        <span /><span>Task</span>
                        <span className="c">Time</span>
                        {isLibrary ? <span className="c">Multiplied</span> : <span>Notes</span>}
                        <span />
                      </div>

                      {sectionTasks.length === 0 && (
                        <div className="bi-task" style={{ gridTemplateColumns: taskCols, opacity: 0.4 }}>
                          <span />
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                            No tasks yet
                          </span>
                          <span /><span /><span />
                        </div>
                      )}

                      {sectionTasks.map(task => {
                        const tIdx = tasks.findIndex(t => t.id === task.id)
                        const saved = !isLibrary && isTaskSaved(task)
                        return (
                          <div
                            key={task.id}
                            className="bi-task"
                            style={{
                              gridTemplateColumns: taskCols,
                              ...(dragOverTaskIdx === tIdx ? { background: 'var(--green-soft)' } : {}),
                              ...(dragTaskIdx === tIdx ? { opacity: 0.35 } : {}),
                            }}
                            draggable
                            onDragStart={e => { e.stopPropagation(); handleTaskDragStart(tIdx) }}
                            onDragOver={e => { e.preventDefault(); setDragOverTaskIdx(tIdx) }}
                            onDrop={e => { e.preventDefault(); handleTaskDrop(tIdx) }}
                            onDragEnd={() => { setDragTaskIdx(null); setDragOverTaskIdx(null) }}
                          >
                            <span style={{ color: 'var(--ink-3)', opacity: 0.6, cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                              <GripSvg />
                            </span>

                            <input
                              className="ak-edit"
                              style={{ padding: 0 }}
                              value={task.name}
                              placeholder="Task description"
                              onChange={e => onTaskName && onTaskName(task.id, e.target.value)}
                              onBlur={e => onTaskName && onTaskName(task.id, e.target.value, true)}
                            />

                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              <TimeControl
                                hrs={task.hrs}
                                hoursPerDay={hoursPerDay}
                                onChange={h => onTaskHrs && onTaskHrs(task.id, h)}
                              />
                            </div>

                            {isLibrary ? (
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <div className="mi-mult">
                                  <button
                                    className={task.isMultiplied ? 'on' : ''}
                                    onClick={() => onTaskMultiplied && onTaskMultiplied(task.id, true)}
                                  >× qty</button>
                                  <button
                                    className={!task.isMultiplied ? 'on' : ''}
                                    onClick={() => onTaskMultiplied && onTaskMultiplied(task.id, false)}
                                  >fixed</button>
                                </div>
                              </div>
                            ) : (
                              <input
                                className="ak-edit"
                                style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}
                                value={task.note || ''}
                                placeholder="Notes…"
                                onChange={e => onTaskNote && onTaskNote(task.id, e.target.value)}
                                onBlur={e => onTaskNote && onTaskNote(task.id, e.target.value, true)}
                              />
                            )}

                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'flex-end' }}>
                              {!isLibrary && (
                                <button
                                  style={{
                                    fontSize: 13, background: 'none', border: 'none', padding: '2px 3px',
                                    cursor: saved ? 'default' : 'pointer',
                                    color: saved ? 'var(--c-adhesives)' : 'var(--ink-3)',
                                  }}
                                  title={saved ? 'In My Items' : 'Save to My Items'}
                                  onClick={() => !saved && onSaveToLibrary && onSaveToLibrary(task)}
                                  tabIndex={-1}
                                >{saved ? '★' : '☆'}</button>
                              )}
                              <button
                                className="ak-iconbtn ak-iconbtn--danger"
                                style={{ width: 22, height: 22, flexShrink: 0 }}
                                onClick={() => onTaskDelete && onTaskDelete(task.id)}
                              >×</button>
                            </div>
                          </div>
                        )
                      })}

                      {/* Add row */}
                      <div style={{ display: 'flex', gap: 8, padding: '7px 16px 10px', borderBottom: '1px solid var(--line)' }}>
                        <button
                          className="add-btn"
                          style={{ fontSize: 12 }}
                          onClick={() => onTaskAdd && onTaskAdd(section)}
                        >+ Add task</button>
                        {!isLibrary && (
                          <button
                            className="add-btn"
                            style={{ fontSize: 12, color: 'var(--green)' }}
                            onClick={() => setMyItemsSection(s => s === section ? null : section)}
                          >+ From My Items</button>
                        )}
                      </div>

                      {/* My Items picker (project context only) */}
                      {myItemsSection === section && (
                        <div style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
                          {catItems.length === 0 ? (
                            <div style={{ padding: '8px 16px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>
                              No My Items tasks matching this section
                            </div>
                          ) : catItems.map(ci => (
                            <div key={ci.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', borderBottom: '1px solid var(--line)' }}>
                              <span style={{ fontSize: 13, flex: 1, color: 'var(--ink)' }}>{ci.name}</span>
                              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{ci.unit_duration || 0}h</span>
                              <button
                                className="ak-btn ak-btn--primary ak-btn--sm"
                                onClick={() => { onAddFromCatalogue && onAddFromCatalogue(ci, section) }}
                              >Add</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            })}

            {/* Add section */}
            {!isLibrary && (
              <div style={{ padding: '10px 16px', borderTop: '1px dashed var(--line-2)' }}>
                {addingSection ? (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      className="ak-input"
                      style={{ flex: 1, maxWidth: 220, fontSize: 13 }}
                      value={newSectionName}
                      autoFocus
                      placeholder="Section name…"
                      onChange={e => setNewSectionName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newSectionName.trim()) {
                          onSectionAdd && onSectionAdd(newSectionName.trim())
                          setAddingSection(false); setNewSectionName('')
                        }
                        if (e.key === 'Escape') { setAddingSection(false); setNewSectionName('') }
                      }}
                    />
                    <button
                      className="ak-btn ak-btn--primary ak-btn--sm"
                      onClick={() => { if (newSectionName.trim()) { onSectionAdd && onSectionAdd(newSectionName.trim()); setAddingSection(false); setNewSectionName('') } }}
                    >Add</button>
                    <button className="ak-btn ak-btn--ghost ak-btn--sm" onClick={() => { setAddingSection(false); setNewSectionName('') }}>Cancel</button>
                  </div>
                ) : (
                  <button className="add-btn" style={{ fontSize: 12 }} onClick={() => setAddingSection(true)}>+ Add section</button>
                )}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="bi-foot">
            <div>
              <div className="bi-foot-label">{isLibrary ? 'Library total' : 'Build & Install Total'}</div>
              <div className="bi-foot-hint">
                {isLibrary ? 'Reference hours per task, resolved at project time' : 'Updates the B&I labour line automatically'}
              </div>
            </div>
            <div className="bi-foot-total">
              {!isLibrary && (
                <div className="bi-foot-cost">
                  £{totalCost.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                </div>
              )}
              <div className="bi-foot-meta">
                {totalDays.toFixed(1)}d · {totalHrs.toFixed(1)} hrs
                {!isLibrary && ` @ £${dayRate}/day`}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  )
}
